import { useMemo } from 'react';
import {
  Stack,
  HStack, 
  Text,
  Icon, 
  Tag,
  TagLabel,
  Tooltip,
  Button,
  IconButton
} from '@chakra-ui/react'
import { Rating } from '@smastrom/react-rating'
import '@smastrom/react-rating/style.css'
import { BiHash } from 'react-icons/bi';
import { MdCall, MdSend } from 'react-icons/md';
import { FaMedal } from "react-icons/fa";
import { AiFillFacebook, AiFillHeart, AiOutlineHeart, AiFillStar, AiOutlineStar } from 'react-icons/ai';
import { FiShare } from "react-icons/fi";
import { useGlobal, useUser } from 'src/context';
import { ShareFab } from 'components/common';
import { featureCodeMap, trackEvent, getDocumentTitle, getDocumentUrl, getGameTypeDisplay, siteTitle } from 'utils';

const SHOW_LIKES_COUNT = 10;

const GameMainFeatures = props => {
  const { 
    gameId,
    title,
    avgRating, 
    totalRatingUsers, 
    currentLikeUsers,
    prominence,
    features, 
    isOutdoorGame,
    isScenarioGame,
    isTheaterGame,
    isBigGame,
    isGameOver, 
    isLimitedTime,
    rsvLink,
    rsvCall,
    onOpenReview
  } = props;

  const { onOpenLogin } = useGlobal();

  const { 
    isLogin,
    isLoadingUser,
    isPostingReview,
    user: { 
      likeGamesIdList, reviewedGamesIdList
    },
    updateList
  } = useUser();

  const isLoading = isLoadingUser || isPostingReview;

  const hasLike = useMemo(() => {
    return likeGamesIdList.includes(gameId);
  }, [likeGamesIdList, gameId]);

  const hasReviewed = useMemo(() => {
    return reviewedGamesIdList.includes(gameId);
  }, [reviewedGamesIdList, gameId]);

  const { typeColor, typeLabel } = getGameTypeDisplay({
    isOutdoorGame,
    isScenarioGame,
    isTheaterGame,
    isBigGame
  })

  const handleLikeToggle = () => {
    trackEvent({
      category: 'Like--GamePage',
      action: 'Click',
      label: `${gameId}-${title}`,
    });

    if (isLogin) {
      updateList('like', gameId)
    } else {
      if (!isLoadingUser) onOpenLogin();
    };
  };

  const handleReviewClick = () => {
    trackEvent({
      category: 'Review_edit--GamePage',
      action: 'Click',
      label: `${gameId}-${title}`,
    });

    if (isLogin) {
      onOpenReview();
    } else {
      if (!isLoadingUser) onOpenLogin();
    }
  };

  const handleRsvLinkClick = () => {
    trackEvent({
      category: "Reservation--GamePage",
      action: "Click",
      label: `${gameId}-${title}-${rsvLink}`,
      value: 2,
    });
  };

  const handleRsvCallClick = () => {
    trackEvent({
      category: "Reservation--GamePage",
      action: "Call",
      label: `${gameId}-${title}-${rsvCall}`,
      value: 10,
    });
  }

  const handleShareClick = async () => {
    if (navigator.share) {
      const shareData = {
        title: getDocumentTitle(),
        text: `我在 ${siteTitle} 找到一款超讚的實境遊戲，分享給你看看！`,
        url: getDocumentUrl()
      }

      try {
        await navigator.share(shareData)

        trackEvent({ 
          category: `Sharing--GamePage`, 
          action: 'NativeShare_fab', 
          label: `${gameId}-${title}`, 
          value: 1 
        })
      } catch (err) {
        console.log(err);
      }
    }
  };

  const isToFacebook = rsvLink && rsvLink.includes('facebook');
  const sanitizedRsvLink = rsvLink + (rsvLink.includes('?') ? '&' : '?') + 'utm_source=EscapeBar&utm_medium=referral&utm_campaign=EscapeBarGamePage';

  return (
    <Stack pt={{ base: 4, lg: 0 }} h='full' justify='space-between'>
      <Stack spacing={{ base: 1, lg: 2 }}>
        {prominence && (
          <HStack 
            bg='secondary.500'
            w='fit-content'
            px={2}
            py={1}
          >
            <Icon as={FaMedal} boxSize={4} color='white' />

            <Text 
              color='white'
              fontSize={{ base: 'md', lg: 'lg' }}
              letterSpacing='1px'
            >
              {prominence}
            </Text>
          </HStack>
        )}

        <HStack spacing={4} flexWrap='wrap'>
          <h1 className='text-2xl lg:text-3xl font-black'>
            {title}
          </h1>
          <Tag
            size={{ base: 'md', lg: 'lg' }}
            variant='outline'
            colorScheme={typeColor}
            >
            <TagLabel>
              {typeLabel}
            </TagLabel>
          </Tag>
          {isGameOver && (
            <Tag
              size={{ base: 'md', lg: 'lg' }}
              variant='outline' 
              colorScheme='gray'
            >
              <TagLabel>
                已謝幕
              </TagLabel>
            </Tag>
          )}
          {(!isGameOver && isLimitedTime) && (
            <Tag
              size={{ base: 'md', lg: 'lg' }}
              variant='outline'
              colorScheme='red'
            >
              <TagLabel>
                期間限定
              </TagLabel>
            </Tag>
          )}
        </HStack>

        <HStack>
          {avgRating > 0 && totalRatingUsers > 0 ? (
            <>
              <Text as='b' fontSize={{ base: 'lg', lg: 'xl' }}>
                {avgRating.toFixed(1)}
              </Text>
              <Rating 
                value={avgRating} 
                style={{ maxWidth: 120 }}
                readOnly 
              />
              <Text>({totalRatingUsers.toLocaleString()})</Text>
            </>
          ) : (
            <Text>尚未有人評價</Text>
          )}
        </HStack>

        {features && features.length > 0 && (
          <HStack spacing={4}>
            {features.map((featureObj, idx) => (
              <Tooltip 
                key={`${featureObj.key}-${idx}`} 
                label={`${parseInt(featureObj.ratio * 100)}% 的玩家選了此項特色`} 
                fontSize='sm'
              >
                <HStack spacing={2}>
                  <Icon as={BiHash} boxSize={4} color='primary.500' />
                  <Text as='b'>
                    {featureCodeMap[featureObj.key]}
                  </Text>
                </HStack>
              </Tooltip>
            ))}
          </HStack>
        )}

        <HStack spacing={2}>
          <Icon as={AiFillHeart} boxSize={4} color='primary.500' />
          <Text as='b'>
          {currentLikeUsers >= SHOW_LIKES_COUNT ? `${currentLikeUsers} 人收藏` : "統計中"}
          </Text>
        </HStack>
      </Stack>

      <Stack spacing={4}>
        <HStack>
          <ShareFab 
            isScreenLgOnly
            sourcePage='GamePage'
            trackTag={`${gameId}-${title}`}
          />
        </HStack>

        <HStack spacing={4} bg='white'>
          <Button 
            variant='solid'
            size={{ base: 'sm', lg: 'md', xl: 'lg' }}
            leftIcon={hasReviewed ? <AiFillStar /> : <AiOutlineStar />} 
            colorScheme='secondary'
            onClick={handleReviewClick}
            isDisabled={isLoading}
          >
            評價
          </Button>

          <Button 
            variant='solid'
            size={{ base: 'sm', lg: 'md', xl: 'lg' }}
            leftIcon={hasLike ? <AiFillHeart /> : <AiOutlineHeart />} 
            colorScheme='primary'
            onClick={handleLikeToggle}
            isDisabled={isLoading}
          >
            收藏
          </Button>

          {(rsvLink && rsvLink.length > 0 && !isGameOver) && (
            <a href={sanitizedRsvLink} target="_blank">
              <Button 
                variant='outline'
                size={{ base: 'sm', lg: 'md', xl: 'lg' }}
                leftIcon={isToFacebook ? <AiFillFacebook/> : <MdSend />} 
                colorScheme={isToFacebook ? 'facebook' : 'primary'} 
                onClick={handleRsvLinkClick}
              >
                預約
              </Button>
            </a>
          )}

          {rsvCall && rsvCall.length > 0 && (  
            <a href={`tel:${rsvCall}`} className='lg:hidden'>
              <IconButton 
                variant='outline'
                size='sm'
                hideFrom='lg'
                icon={<MdCall />} 
                colorScheme='primary' 
                onClick={handleRsvCallClick}
              />
            </a>
          )}

          <IconButton 
            variant='outline'
            size='sm'
            hideFrom='lg'
            icon={<FiShare />} 
            colorScheme='primary' 
            onClick={handleShareClick}
          />
        </HStack>
      </Stack>
    </Stack>
  )
}

export default GameMainFeatures;